/**
 * Athlete-facing effort from the stored prescription, not the scheduling role.
 * PRIMARY_KEY can be an easy aerobic ride. The label follows the work targets.
 */

const LABELS = {
  RECOVERY: "Recovery",
  EASY: "Easy",
  MODERATE: "Moderate",
  HARD: "Hard",
};

const RANK = { RECOVERY: 1, EASY: 2, MODERATE: 3, HARD: 4 };

const RECOVERY_PURPOSES = new Set(["RECOVERY", "POST_EVENT_RECOVERY"]);

const HARD_PURPOSES = new Set([
  "THRESHOLD",
  "VO2",
  "ANAEROBIC",
  "SPRINT",
  "MUSCULAR_ENDURANCE",
  "FATIGUE_RESISTANCE",
  "EVENT_SPECIFIC",
  "ASSESSMENT",
  "PACE_CONTROL",
]);

const SCHEDULING_ROLES = new Set([
  "PRIMARY_KEY",
  "SECONDARY_KEY",
  "LONG_SESSION",
  "AEROBIC_SUPPORT",
  "KEY",
  "SUPPORT",
  "OPTIONAL",
  "MAINTENANCE",
]);

const trainingPurpose = (session) => {
  const raw = session?.objective || session?.primary_purpose || "";
  const purpose = String(raw).toUpperCase();
  if (!purpose || SCHEDULING_ROLES.has(purpose)) return "";
  return purpose;
};

const classFromTarget = (intensity) => {
  if (!intensity || typeof intensity !== "object") return null;
  const unit = String(intensity.unit || intensity.method || "").toUpperCase();
  const bound = intensity.upper ?? intensity.lower;
  if (bound == null || Number.isNaN(Number(bound))) return null;
  const value = Number(bound);
  if (unit === "RPE_0_10" || unit === "RPE") {
    if (value <= 2) return "RECOVERY";
    if (value <= 4) return "EASY";
    if (value <= 7) return "MODERATE";
    return "HARD";
  }
  if (unit === "FTP_PERCENT" || unit === "POWER_FTP_PCT") {
    if (value < 76) return "EASY";
    if (value <= 90) return "MODERATE";
    return "HARD";
  }
  if (unit === "HR_ZONE" || unit === "PACE_ZONE") {
    if (value <= 2) return "EASY";
    if (value === 3) return "MODERATE";
    return "HARD";
  }
  return null;
};

const collectSteps = (steps, into) => {
  if (!Array.isArray(steps)) return;
  for (const step of steps) {
    if (!step || typeof step !== "object") continue;
    into.push(step);
    collectSteps(step.substeps, into);
  }
};

const hardestFromSession = (session) => {
  const steps = [];
  collectSteps(session?.main_set, steps);
  collectSteps(session?.prescription_snapshot?.main_set, steps);
  let hardest = null;
  for (const step of steps) {
    const found = classFromTarget(step.intensity);
    if (!found) continue;
    if (!hardest || RANK[found] > RANK[hardest]) hardest = found;
  }
  return hardest;
};

const classFromPurpose = (purpose) => {
  if (RECOVERY_PURPOSES.has(purpose)) return "RECOVERY";
  if (purpose === "AEROBIC_BASE" || purpose === "ENDURANCE") return "EASY";
  if (purpose === "TEMPO") return "MODERATE";
  if (HARD_PURPOSES.has(purpose)) return "HARD";
  if (purpose === "MOBILITY" || purpose === "TECHNIQUE" || purpose === "SKILL") {
    return "EASY";
  }
  return null;
};

export const prescriptionIntensityClass = (session) => {
  if (!session) return null;
  const purpose = trainingPurpose(session);
  if (RECOVERY_PURPOSES.has(purpose)) return "RECOVERY";
  return hardestFromSession(session) || classFromPurpose(purpose);
};

export const prescriptionIntensityLabel = (session) => {
  const value = prescriptionIntensityClass(session);
  return value ? LABELS[value] : null;
};
